// useProjectFloorPlanItems.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Utilities
import {
  applyPlacementOverride,
  buildPlacementPatch,
  listRoomIdsOnFloor,
  PURCHASE_ORDER_ITEM_TYPE_KEY,
  readItemFloorName,
  readItemRoomKey,
  readItemRoomName,
  resolveFloorNameForRoom,
} from 'nodetypes/project/floorplan/floorPlanItemPlacement';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Hooks
import useNodeDescendants from 'node/hooks/useNodeDescendants';

/**
 * useProjectFloorPlanItems
 *
 * Loads project-related PURCHASE_ORDER_ITEM nodes and assigns room placement.
 *
 * @param {object} params
 * @param {object|null} params.projectNode
 * @returns {{
 *   items: object[],
 *   isLoading: boolean,
 *   assignItemToRoom: Function,
 *   clearItemPlacement: Function,
 *   clearPlacementsForRoom: Function,
 *   renamePlacementsForRoom: Function,
 *   renamePlacementsForFloor: Function,
 * }}
 */
export default function useProjectFloorPlanItems({ projectNode = null } = {}) {
  // ----- CONTEXT ----- //

  // User Context
  const { user } = useUser();

  // ----- STATE ----- //

  // Optimistic roomKey/room/floor overlays keyed by item key
  const [overrides, setOverrides] = useState({});

  // ----- HOOKS ----- //

  // Descendant nodes under the project (includes PO items).
  const { descendantNodes } = useNodeDescendants({
    nodeKey: projectNode?.key || null,
    user,
    parentNode: projectNode,
    enabled: Boolean(projectNode?.key && user),
  });

  // ----- MEMOS ----- //

  // Remote PO items under the project.
  const remoteItems = useMemo(() => (
    (Array.isArray(descendantNodes) ? descendantNodes : []).filter(
      (node) => String(node?.type || '').trim().toUpperCase() === PURCHASE_ORDER_ITEM_TYPE_KEY,
    )
  ), [descendantNodes]);

  // Items with optimistic placement overlays applied.
  const items = useMemo(() => (
    remoteItems.map((item) => applyPlacementOverride(item, overrides[item.key] || null))
  ), [overrides, remoteItems]);

  // Descendants subscribe emits [] quickly; treat as ready once project+user are set.
  const isLoading = false;

  // ----- EFFECTS ----- //

  // Drop overrides once remote catches up on roomKey/room/floor.
  useEffect(() => {
    setOverrides((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const next = { ...prev };
      let changed = false;
      keys.forEach((itemKey) => {
        const remote = remoteItems.find((row) => row.key === itemKey);
        if (!remote) return;
        const desired = prev[itemKey];
        const roomKeyMatched = readItemRoomKey(remote) === String(desired?.roomKey || '').trim();
        const roomMatched = readItemRoomName(remote) === String(desired?.room || '').trim();
        const floorMatched = readItemFloorName(remote) === String(desired?.floor || '').trim();
        if (roomKeyMatched && roomMatched && floorMatched) {
          delete next[itemKey];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [remoteItems]);

  // ----- HANDLERS ----- //

  /**
   * persistPlacement
   *
   * Writes roomKey/room/floor on a PO item with an optimistic overlay.
   *
   * @param {object} item
   * @param {{ roomKey: string, room: string, floor: string }} placement
   */
  const persistPlacement = useCallback(async (item, placement) => {
    if (!item?.key) return;
    setOverrides((prev) => ({
      ...prev,
      [item.key]: {
        roomKey: String(placement.roomKey || '').trim(),
        room: String(placement.room || '').trim(),
        floor: String(placement.floor || '').trim(),
      },
    }));
    try {
      await updateNode(item.key, buildPlacementPatch(item, placement), { actor: user });
    } catch (error) {
      console.error('Floor plan item placement failed', error);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
      throw error;
    }
  }, [user]);

  /**
   * assignItemToRoom
   *
   * @param {object} item
   * @param {{ id: string, name: string }} room
   * @param {object|null|undefined} [layout] - Used to resolve denormalized floor name
   */
  const assignItemToRoom = useCallback(async (item, room, layout = null) => {
    if (!item?.key || !room?.id) return;
    await persistPlacement(item, {
      roomKey: room.id,
      room: room.name || '',
      floor: resolveFloorNameForRoom(layout, room.id),
    });
  }, [persistPlacement]);

  /**
   * clearItemPlacement
   *
   * @param {object} item
   */
  const clearItemPlacement = useCallback(async (item) => {
    if (!item?.key) return;
    await persistPlacement(item, { roomKey: '', room: '', floor: '' });
  }, [persistPlacement]);

  /**
   * clearPlacementsForRoom
   *
   * @param {string} roomId
   */
  const clearPlacementsForRoom = useCallback(async (roomId) => {
    const id = String(roomId || '').trim();
    if (!id) return;
    const targets = items.filter((item) => readItemRoomKey(item) === id);
    await Promise.all(targets.map((item) => persistPlacement(item, {
      roomKey: '',
      room: '',
      floor: '',
    })));
  }, [items, persistPlacement]);

  /**
   * renamePlacementsForRoom
   *
   * @param {string} roomId
   * @param {string} roomName
   * @param {object|null|undefined} [layout]
   */
  const renamePlacementsForRoom = useCallback(async (roomId, roomName, layout = null) => {
    const id = String(roomId || '').trim();
    const name = String(roomName || '').trim();
    if (!id || !name) return;
    const floorName = resolveFloorNameForRoom(layout, id);
    const targets = items.filter((item) => readItemRoomKey(item) === id);
    await Promise.all(targets.map((item) => persistPlacement(item, {
      roomKey: id,
      room: name,
      floor: floorName || readItemFloorName(item),
    })));
  }, [items, persistPlacement]);

  /**
   * renamePlacementsForFloor
   *
   * Updates denormalized floor on items assigned to rooms on this floor.
   *
   * @param {string} floorId
   * @param {string} floorName
   * @param {object|null|undefined} layout
   */
  const renamePlacementsForFloor = useCallback(async (floorId, floorName, layout = null) => {
    const name = String(floorName || '').trim();
    if (!name) return;
    const roomIds = new Set(listRoomIdsOnFloor(layout, floorId));
    if (!roomIds.size) return;
    const targets = items.filter((item) => roomIds.has(readItemRoomKey(item)));
    await Promise.all(targets.map((item) => persistPlacement(item, {
      roomKey: readItemRoomKey(item),
      room: readItemRoomName(item),
      floor: name,
    })));
  }, [items, persistPlacement]);

  return {
    items,
    isLoading,
    assignItemToRoom,
    clearItemPlacement,
    clearPlacementsForRoom,
    renamePlacementsForRoom,
    renamePlacementsForFloor,
  };
}
